// @ts-nocheck
import { api, type CatalogEntryDTO } from "../api/client";
import { debounce } from "../utils/debounce";
import { dtoToLegacy } from "../utils/dto";
import { mapState, mapRefs } from "../map/state";
import { ensureHeadstoneInDOM } from "../map/holes";
import { restoreGrave, openViewModal } from "../map/grave-render";
import { update } from "../map/camera";
import { renderPetPhotoElement } from "../utils/pet-photo-placeholder";
import {
  candleIconSvg,
  featuredCandleIconSvg,
  featuredFlowerIconSvg,
  flowerIconSvg,
  heartIconSvg,
  starIconSvg,
} from "../utils/tribute-icons";
import { showView, getViewContainer } from "./view-router";
import { refreshAds, tempioAdItemHtml } from "./ads";

let tempioViewMounted = false;

function ensureTempioView(): void {
  const host = getViewContainer("tempio");
  if (!host || tempioViewMounted) return;

  host.innerHTML = /* html */ `
    <header class="tempio-header">
      <button type="button" class="back-chip tempio-back" onclick="goHome()">
        <i data-lucide="arrow-left"></i><span>Torna al giardino</span>
      </button>
      <div class="tempio-header__inner">
        <div class="eyebrow">In memoria</div>
        <h1 class="view-title">Il Tempio dei Ricordi</h1>
        <p class="view-subtitle">I memoriali più vissuti dalla community</p>
        <label class="search-pill tempio-search tempio-search--mobile">
          <i data-lucide="search"></i>
          <input
            id="memorial-catalog-search"
            type="search"
            placeholder="Cerca per nome o epitaffio…"
            autocomplete="off"
            aria-label="Cerca tra i ricordi"
          />
        </label>
      </div>
    </header>
    <div id="memorial-catalog-body" class="memorial-catalog-body pscroll" aria-busy="true">
      <p class="memorial-catalog-status memorial-catalog-status--sr-only" aria-live="polite">Caricamento...</p>
      ${buildMemorialCatalogSkeletonHtml(6)}
    </div>
  `;

  tempioViewMounted = true;
}

type CatalogState = {
  items: CatalogEntryDTO[];
  nextCursor: string | null;
  featuredId: string | null;
};

const catalogState: CatalogState = {
  items: [],
  nextCursor: null,
  featuredId: null,
};

let catalogQuery = "";
let catalogRequestId = 0;
let catalogAbort: AbortController | null = null;
let catalogLoading = false;
let searchHandlerAttached = false;
let catalogShellMounted = false;

type CatalogLoadingMode = "initial" | "search";

const CATALOG_LOADING_MESSAGES: Record<CatalogLoadingMode, string> = {
  initial: "Caricamento...",
  search: "Ricerca...",
};

function buildMemorialCatalogSkeletonItemHtml(): string {
  return `
    <div class="memorial-catalog-skeleton-card" aria-hidden="true">
      <span class="memorial-catalog-skeleton-card__hero"></span>
      <span class="memorial-catalog-skeleton-card__body">
        <span class="memorial-catalog-skeleton-line memorial-catalog-skeleton-line--name"></span>
        <span class="memorial-catalog-skeleton-line memorial-catalog-skeleton-line--date"></span>
        <span class="memorial-catalog-skeleton-line memorial-catalog-skeleton-line--epitaph"></span>
      </span>
    </div>
  `;
}

export function buildMemorialCatalogSkeletonItemsHtml(count = 3): string {
  return Array.from({ length: count }, buildMemorialCatalogSkeletonItemHtml).join("");
}

export function buildMemorialCatalogSkeletonHtml(count = 3): string {
  return `<div class="memorial-catalog-loader memorial-catalog-loader--grid">${buildMemorialCatalogSkeletonItemsHtml(count)}</div>`;
}

function hideCatalogLoadingChrome() {
  document.getElementById("memorial-catalog-global-empty")?.setAttribute("hidden", "");
  document.getElementById("memorial-catalog-search-empty")?.setAttribute("hidden", "");
  document.getElementById("memorial-catalog-featured")?.setAttribute("hidden", "");
  document.getElementById("memorial-catalog-grid-head")?.setAttribute("hidden", "");
  document.getElementById("memorial-catalog-list")?.setAttribute("hidden", "");
  document.getElementById("memorial-catalog-load-more")?.setAttribute("hidden", "");
  const tempioAd = document.getElementById("tempio-ad-slot");
  if (tempioAd) {
    tempioAd.setAttribute("hidden", "");
    tempioAd.setAttribute("data-ads-ready", "0");
  }
  document.querySelectorAll("[data-tempio-ad-host]").forEach((el) => el.remove());
}

function showMemorialCatalogLoading(mode: CatalogLoadingMode) {
  const body = document.getElementById("memorial-catalog-body");
  if (!body) return;

  const message = CATALOG_LOADING_MESSAGES[mode];
  body.setAttribute("aria-busy", "true");
  setCatalogStatus(message);

  if (!catalogShellMounted) {
    body.innerHTML = `
      <p id="memorial-catalog-status" class="memorial-catalog-status memorial-catalog-status--sr-only" aria-live="polite">${message}</p>
      ${buildMemorialCatalogSkeletonHtml(6)}
    `;
    return;
  }

  hideCatalogLoadingChrome();
  const featured = document.getElementById("memorial-catalog-featured");
  const list = document.getElementById("memorial-catalog-list");
  if (featured) featured.hidden = true;
  if (list) {
    list.hidden = false;
    list.innerHTML = buildMemorialCatalogSkeletonItemsHtml(6);
  }
}

function hideMemorialCatalogLoading() {
  const body = document.getElementById("memorial-catalog-body");
  body?.removeAttribute("aria-busy");
  setCatalogStatus(null);
}

export function closeMemorialCatalogModal() {
  catalogAbort?.abort();
  showView("garden");
}

function buildDateLabel(entry: CatalogEntryDTO): string {
  const legacy = dtoToLegacy(entry);
  return legacy.fullDateLabel || legacy.dateLabel || "";
}

function attachCardVisit(el: HTMLElement, entry: CatalogEntryDTO) {
  const visit = () => visitHeadstoneFromCatalog(entry);
  el.addEventListener("click", visit);
  el.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      visit();
    }
  });
}

function buildTributeBadges(
  candleCount: number,
  flowerCount: number,
  variant: "card" | "featured",
): HTMLElement {
  const meta = document.createElement("div");
  meta.className = variant === "featured" ? "featured-hero__meta" : "memorial-card__meta";

  const candle = document.createElement("span");
  candle.className = variant === "featured" ? "featured-hero__badge featured-hero__badge--candle" : "candle-badge";
  candle.title = "Candele accese";
  candle.innerHTML = `${variant === "featured" ? featuredCandleIconSvg() : candleIconSvg()} ${candleCount}`;

  const flower = document.createElement("span");
  flower.className = variant === "featured" ? "featured-hero__badge featured-hero__badge--flower" : "flower-badge";
  flower.title = "Fiori lasciati";
  flower.innerHTML = `${variant === "featured" ? featuredFlowerIconSvg() : flowerIconSvg()} ${flowerCount}`;

  meta.appendChild(candle);
  meta.appendChild(flower);
  return meta;
}

function renderFeaturedHero(entry: CatalogEntryDTO): HTMLElement {
  const legacy = dtoToLegacy(entry);
  const hero = document.createElement("article");
  hero.className = "featured-hero";
  hero.dataset.headstoneId = entry.id;
  hero.setAttribute("role", "button");
  hero.setAttribute("tabindex", "0");

  const bg = document.createElement("div");
  bg.className = "featured-hero__bg";
  bg.appendChild(
    renderPetPhotoElement({
      variant: "view-hero",
      photoUrl: legacy.photo,
      seed: entry.id,
      name: legacy.name,
    }),
  );
  hero.appendChild(bg);

  const content = document.createElement("div");
  content.className = "featured-hero__content";

  const tag = document.createElement("div");
  tag.className = "featured-hero__tag";
  tag.innerHTML = `${starIconSvg()} Memoriale in evidenza`;
  content.appendChild(tag);

  const nameEl = document.createElement("div");
  nameEl.className = "featured-hero__name";
  nameEl.textContent = legacy.name;
  content.appendChild(nameEl);

  const dateLabel = buildDateLabel(entry);
  if (dateLabel) {
    const rangeEl = document.createElement("div");
    rangeEl.className = "featured-hero__range";
    rangeEl.textContent = dateLabel;
    content.appendChild(rangeEl);
  }

  if (legacy.epitaph) {
    const ep = document.createElement("div");
    ep.className = "featured-hero__epitaph";
    ep.textContent = `“${legacy.epitaph}”`;
    content.appendChild(ep);
  }

  content.appendChild(buildTributeBadges(entry.candleCount ?? 0, entry.flowerCount ?? 0, "featured"));
  hero.appendChild(content);

  attachCardVisit(hero, entry);
  return hero;
}

function renderCatalogItem(entry: CatalogEntryDTO): HTMLElement {
  const legacy = dtoToLegacy(entry);
  const card = document.createElement("div");
  card.className = "memorial-card";
  card.dataset.headstoneId = entry.id;
  card.setAttribute("role", "button");
  card.setAttribute("tabindex", "0");

  const hero = document.createElement("div");
  hero.className = "memorial-card__hero";
  hero.appendChild(
    renderPetPhotoElement({
      variant: "hero",
      photoUrl: legacy.photo,
      seed: entry.id,
      name: legacy.name,
    }),
  );

  const heart = document.createElement("div");
  heart.className = "memorial-card__heart";
  heart.innerHTML = heartIconSvg();
  hero.appendChild(heart);
  card.appendChild(hero);

  const body = document.createElement("div");
  body.className = "memorial-card__body";

  const nameEl = document.createElement("div");
  nameEl.className = "memorial-card__name";
  nameEl.textContent = legacy.name;
  body.appendChild(nameEl);

  const dateLabel = buildDateLabel(entry);
  if (dateLabel) {
    const dateEl = document.createElement("div");
    dateEl.className = "memorial-card__range";
    dateEl.textContent = dateLabel;
    body.appendChild(dateEl);
  }

  if (legacy.epitaph) {
    const ep = document.createElement("div");
    ep.className = "memorial-card__epitaph";
    ep.textContent = `“${legacy.epitaph}”`;
    body.appendChild(ep);
  }

  body.appendChild(buildTributeBadges(entry.candleCount ?? 0, entry.flowerCount ?? 0, "card"));
  card.appendChild(body);

  attachCardVisit(card, entry);
  return card;
}

function ensureCatalogShell() {
  const body = document.getElementById("memorial-catalog-body");
  if (!body || catalogShellMounted) return;

  body.innerHTML = `
    <p id="memorial-catalog-status" class="memorial-catalog-status memorial-catalog-status--sr-only" hidden aria-live="polite">Ricerca...</p>
    <div id="memorial-catalog-featured" class="memorial-catalog-featured" hidden></div>
    <div id="tempio-ad-slot" class="tempio-ad-slot" hidden data-ads-ready="0"></div>
    <div id="memorial-catalog-grid-head" class="memorial-catalog-grid-head" hidden>
      <span class="memorial-catalog-grid-head__label">Tutti i memoriali</span>
      <span id="memorial-catalog-count" class="memorial-catalog-grid-head__count"></span>
    </div>
    <div id="memorial-catalog-list" class="memorial-catalog-list" hidden></div>
    <button type="button" id="memorial-catalog-load-more" class="memorial-catalog-load-more" hidden>Carica altri</button>
    <p id="memorial-catalog-global-empty" class="memorial-catalog-empty" hidden>
      Il mausoleo è ancora silenzioso: nessun memoriale da mostrare.
    </p>
    <p id="memorial-catalog-search-empty" class="memorial-catalog-empty" hidden>
      Nessun memoriale corrisponde alla ricerca.
    </p>
  `;

  document.getElementById("memorial-catalog-load-more")?.addEventListener("click", (e) => {
    e.stopPropagation();
    loadMoreCatalog().catch(() => null);
  });

  catalogShellMounted = true;
}

function getFeaturedEntry(): CatalogEntryDTO | null {
  if (!catalogState.featuredId) return null;
  return catalogState.items.find((item) => item.id === catalogState.featuredId) ?? null;
}

function getGridEntries(): CatalogEntryDTO[] {
  if (!catalogState.featuredId) return catalogState.items;
  return catalogState.items.filter((item) => item.id !== catalogState.featuredId);
}

function updateCatalogCount() {
  const countEl = document.getElementById("memorial-catalog-count");
  if (!countEl) return;
  const n = catalogState.items.length;
  countEl.textContent = `${n} ${n === 1 ? "ricordo custodito" : "ricordi custoditi"}`;
}

function updateEmptyState() {
  const globalEmpty = document.getElementById("memorial-catalog-global-empty");
  const searchEmpty = document.getElementById("memorial-catalog-search-empty");
  if (!globalEmpty || !searchEmpty) return;

  const hasItems = catalogState.items.length > 0;
  globalEmpty.hidden = catalogQuery || hasItems || catalogLoading;
  searchEmpty.hidden = !catalogQuery || hasItems || catalogLoading;
}

function renderFeatured() {
  const host = document.getElementById("memorial-catalog-featured");
  if (!host) return;

  const entry = getFeaturedEntry();
  host.replaceChildren();
  if (entry) {
    host.appendChild(renderFeaturedHero(entry));
    host.hidden = false;
  } else {
    host.hidden = true;
  }
}

function renderGrid(append: boolean) {
  const list = document.getElementById("memorial-catalog-list");
  const gridHead = document.getElementById("memorial-catalog-grid-head");
  if (!list) return;

  const gridEntries = getGridEntries();
  const hasAny = catalogState.items.length > 0;

  if (gridHead) gridHead.hidden = !hasAny;
  list.hidden = !hasAny;

  if (!append) list.innerHTML = "";

  const start = append ? list.querySelectorAll(".memorial-card").length : 0;
  for (let i = start; i < gridEntries.length; i++) {
    list.appendChild(renderCatalogItem(gridEntries[i]));
  }

  syncTempioGridAd(list);
  updateCatalogCount();
  updateEmptyState();

  const loadBtn = document.getElementById("memorial-catalog-load-more");
  if (loadBtn) loadBtn.hidden = !catalogState.nextCursor;
}

function renderCatalog(append: boolean) {
  renderFeatured();
  renderGrid(append);
  syncTempioAdReady();
}

function catalogHasPublisherContent(): boolean {
  return catalogState.items.length >= 2 || !!getFeaturedEntry();
}

function syncTempioAdReady() {
  const slot = document.getElementById("tempio-ad-slot");
  if (!slot) return;
  const ready = catalogHasPublisherContent();
  slot.setAttribute("data-ads-ready", ready ? "1" : "0");
  if (!ready) slot.hidden = true;
  refreshAds();
}

function syncTempioGridAd(list: HTMLElement) {
  const cards = [...list.querySelectorAll(".memorial-card")];
  const existing = list.querySelector("[data-tempio-ad-host]");
  if (cards.length < 2 || !catalogHasPublisherContent()) {
    existing?.remove();
    return;
  }
  if (existing) return;
  const html = tempioAdItemHtml();
  if (!html) return;
  const wrap = document.createElement("div");
  wrap.innerHTML = html.trim();
  const ad = wrap.firstElementChild;
  if (ad) cards[1].after(ad);
}

function setCatalogStatus(message: string | null) {
  const status = document.getElementById("memorial-catalog-status");
  if (!status) return;
  if (message) {
    status.textContent = message;
    status.hidden = false;
  } else {
    status.hidden = true;
  }
}

async function fetchCatalogFromApi(opts: { memoryCursor?: string; signal?: AbortSignal }) {
  const query = {
    q: catalogQuery || undefined,
    memoryCursor: opts.memoryCursor,
    necrologyLimit: 1,
  };

  const { data, error } = await api.GET("/headstones/catalog", {
    params: { query },
    signal: opts.signal,
  });

  if (error) throw error;
  return data?.inMemory ?? { items: [], nextCursor: null };
}

async function fetchCatalog(opts: { reset: boolean; append?: boolean }) {
  const requestId = ++catalogRequestId;
  catalogAbort?.abort();
  catalogAbort = new AbortController();
  const signal = catalogAbort.signal;

  catalogLoading = true;
  if (opts.reset) {
    catalogState.items = [];
    catalogState.nextCursor = null;
    catalogState.featuredId = null;
    ensureCatalogShell();
    showMemorialCatalogLoading(catalogQuery ? "search" : "initial");
  } else if (opts.append) {
    const btn = document.getElementById("memorial-catalog-load-more");
    if (btn) btn.disabled = true;
  }

  try {
    const data = await fetchCatalogFromApi({
      memoryCursor: opts.append ? catalogState.nextCursor ?? undefined : undefined,
      signal,
    });

    if (requestId !== catalogRequestId) return;

    if (opts.reset) {
      catalogState.items = data.items ?? [];
      catalogState.nextCursor = data.nextCursor ?? null;
      catalogState.featuredId = catalogState.items[0]?.id ?? null;
    } else if (opts.append) {
      catalogState.items.push(...(data.items ?? []));
      catalogState.nextCursor = data.nextCursor ?? null;
    }

    ensureCatalogShell();
    renderCatalog(!!opts.append);
    hideMemorialCatalogLoading();
  } catch (err) {
    if (requestId !== catalogRequestId) return;
    if (err?.name === "AbortError") return;

    const body = document.getElementById("memorial-catalog-body");
    if (opts.reset && body) {
      catalogShellMounted = false;
      body.innerHTML = `<p class="memorial-catalog-error">Errore: ${err?.message ?? "impossibile caricare i ricordi"}</p>`;
    }
    hideMemorialCatalogLoading();
  } finally {
    if (requestId === catalogRequestId) {
      catalogLoading = false;
      const loadBtn = document.getElementById("memorial-catalog-load-more");
      if (loadBtn) loadBtn.disabled = false;
      hideMemorialCatalogLoading();
      updateEmptyState();
    }
  }
}

function applyCatalogSearch(query: string) {
  catalogQuery = query.trim();
  fetchCatalog({ reset: true }).catch(() => null);
}

const debouncedApplyCatalogSearch = debounce(applyCatalogSearch, 300);

function loadMoreCatalog() {
  if (!catalogState.nextCursor || catalogLoading) return;
  return fetchCatalog({ reset: false, append: true });
}

function visitHeadstoneFromCatalog(entry: CatalogEntryDTO) {
  const legacy = dtoToLegacy(entry);
  const { hole, holeId, x, y } = ensureHeadstoneInDOM(entry.x, entry.y);
  mapState.camX = x;
  mapState.camY = y;
  update();
  if (hole) {
    mapState.gravesData[holeId] = legacy;
    if (legacy.id) mapState.graveIdToHole.set(legacy.id, holeId);
    restoreGrave(hole, legacy);
    hole.classList.add("pulse");
    setTimeout(() => hole.classList.remove("pulse"), 900);
    closeMemorialCatalogModal();
    openViewModal(hole);
  }
}

function attachSearchHandler() {
  if (searchHandlerAttached) return;
  const input = document.getElementById("memorial-catalog-search");
  if (!input) return;
  searchHandlerAttached = true;

  input.addEventListener("input", () => {
    ensureCatalogShell();
    showMemorialCatalogLoading("search");
    debouncedApplyCatalogSearch(input.value);
  });
}

async function runCatalog(query: string) {
  ensureTempioView();
  showView("tempio");

  const body = document.getElementById("memorial-catalog-body");
  const search = document.getElementById("memorial-catalog-search") as HTMLInputElement | null;
  if (!body) return;

  catalogQuery = (query || "").trim();
  if (search) search.value = query || "";
  catalogShellMounted = false;
  body.setAttribute("aria-busy", "true");
  body.innerHTML = `
    <p class="memorial-catalog-status memorial-catalog-status--sr-only" aria-live="polite">Caricamento...</p>
    ${buildMemorialCatalogSkeletonHtml(6)}
  `;
  attachSearchHandler();

  await fetchCatalog({ reset: true });
}

export async function openMemorialCatalog() {
  await runCatalog("");
}

export async function openCatalogWithSearch(query: string) {
  await runCatalog(query);
}

function onTempleActivate(e: Event) {
  e.stopPropagation();
  e.preventDefault();
  openMemorialCatalog().catch(() => null);
}

export function initTempleCatalog() {
  const temple = mapRefs.temple ?? document.getElementById("temple");
  if (!temple) return;

  temple.setAttribute("role", "button");
  temple.setAttribute("tabindex", "0");
  temple.setAttribute("aria-label", "Apri il tempio dei ricordi");

  temple.addEventListener("click", onTempleActivate);
  temple.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      onTempleActivate(e);
    }
  });
}
